import React from 'react';
import { formatString } from '@deadpan/shared';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';
import { GameTheoryWheelForceSlider } from './GameTheoryWheelForceSlider';
import {
  GameTheoryWheelWheel,
  liveHeadTail,
  useWheelNeedleAngle,
} from './GameTheoryWheelWheel';

export const GameTheoryWheelGame: React.FC = () => {
  const { socket, currentRoom, gameState, myGameTheoryWheelForce, myPlayerId } = useSocket();
  const t = useTranslations();
  const gtw = t.games.gameTheoryWheel;
  const [force, setForce] = React.useState(myGameTheoryWheelForce ?? 1);

  const stage = gameState?.gameTheoryWheelStage;
  const round = currentRoom?.currentRound ?? 1;
  const submittedIds = gameState?.gameTheoryWheelSubmittedPlayerIds ?? [];
  const hasSubmitted = Boolean(myPlayerId && submittedIds.includes(myPlayerId));
  const spin = gameState?.gameTheoryWheelSpin;
  const needleAngle = useWheelNeedleAngle(
    stage,
    spin?.totalRotationDeg,
    spin?.startedAt,
    spin?.endsAt,
    spin?.finalAngleDeg,
  );

  React.useEffect(() => {
    setForce(myGameTheoryWheelForce ?? 1);
  }, [round]);

  React.useEffect(() => {
    if (myGameTheoryWheelForce != null) {
      setForce(myGameTheoryWheelForce);
    }
  }, [myGameTheoryWheelForce]);

  if (!gameState || !currentRoom) {
    return null;
  }

  const sectorIds = gameState.gameTheoryWheelSectorPlayerIds ?? [];
  const playersById = Object.fromEntries(currentRoom.players.map((p) => [p.id, p]));
  const participants = currentRoom.players.filter((p) => !p.isSpectator);
  const livePair = liveHeadTail(sectorIds, needleAngle);
  const askerId = stage === 'result' ? gameState.gameTheoryWheelAskerId : undefined;
  const answererId = stage === 'result' ? gameState.gameTheoryWheelAnswererId : undefined;
  const highlightAsker = askerId ?? livePair.headId;
  const highlightAnswerer = answererId ?? livePair.tailId;
  const isHost = currentRoom.hostId === myPlayerId;
  const isSpectator = currentRoom.players.find((p) => p.id === myPlayerId)?.isSpectator;
  const canSetForce = stage === 'force' && !isSpectator;
  const askerName = askerId ? playersById[askerId]?.name ?? '—' : '—';
  const answererName = answererId ? playersById[answererId]?.name ?? '—' : '—';

  const handleConfirm = () => {
    if (!socket || !canSetForce) return;
    socket.emit('client:submit-game-theory-wheel-force', force);
  };

  const handleNextRound = () => {
    if (!socket || !isHost) return;
    socket.emit('client:game-theory-wheel-next-round');
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <h2 className="text-[clamp(1.5rem,8vw,3rem)] font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent text-center mb-1">
        {gtw.title}
      </h2>
      <p className="text-xs sm:text-sm text-gray-500 mb-3">
        {formatString(gtw.roundLabel, { round: String(round) })}
      </p>

      <GameTheoryWheelWheel
        sectorPlayerIds={sectorIds}
        playersById={playersById}
        selfId={myPlayerId ?? undefined}
        needleAngleDeg={needleAngle}
        highlightAskerId={highlightAsker}
        highlightAnswererId={highlightAnswerer}
      />

      {stage === 'force' && (
        <div className="w-full mt-4 flex flex-col items-center gap-3">
          <p className="text-sm sm:text-base text-gray-700 text-center">{gtw.setForceInstruction}</p>
          {canSetForce ? (
            <>
              <GameTheoryWheelForceSlider value={force} onChange={setForce} />
              <button
                type="button"
                onClick={handleConfirm}
                className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                {hasSubmitted ? gtw.updateForce : gtw.confirmForce}
              </button>
              {hasSubmitted && (
                <p className="text-sm font-semibold text-green-700">{gtw.forceSubmitted}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-600 text-center">{gtw.waitingForOthers}</p>
          )}
          <p className="text-sm text-gray-600">
            {formatString(gtw.submittedProgress, {
              submitted: String(submittedIds.length),
              total: String(participants.length),
            })}
          </p>
        </div>
      )}

      {stage === 'spinning' && (
        <p className="mt-4 text-lg sm:text-xl font-bold text-indigo-700">{gtw.spinning}</p>
      )}

      {stage === 'result' && (
        <div className="w-full mt-4 space-y-4">
          <h3 className="text-xl sm:text-2xl font-bold text-center text-gray-900">{gtw.resultTitle}</h3>
          <p className="text-center text-base sm:text-lg font-semibold text-gray-800">
            {formatString(gtw.askPrompt, { asker: askerName, answerer: answererName })}
          </p>
          {myPlayerId === askerId && (
            <p className="text-center text-sm font-semibold text-indigo-700">{gtw.youAreAsker}</p>
          )}
          {myPlayerId === answererId && (
            <p className="text-center text-sm font-semibold text-pink-700">{gtw.youAreAnswerer}</p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-center">
              <p className="text-xs font-semibold text-red-700">{gtw.askerLabel}</p>
              <p className="text-lg font-bold text-red-900 break-words">{askerName}</p>
            </div>
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-center">
              <p className="text-xs font-semibold text-amber-700">{gtw.answererLabel}</p>
              <p className="text-lg font-bold text-amber-900 break-words">{answererName}</p>
            </div>
          </div>
          {spin && (
            <p className="text-center text-sm text-gray-600">
              {formatString(gtw.totalForceLabel, {
                force: String(spin.totalForce),
                degrees: String(Math.round(spin.totalRotationDeg * 10) / 10),
              })}
            </p>
          )}
          <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-3">
            <h4 className="text-sm font-bold text-gray-800 mb-2 text-center">{gtw.forcesTitle}</h4>
            <ul className="space-y-1">
              {participants.map((player) => (
                <li key={player.id} className="flex justify-between text-sm text-gray-800">
                  <span className="truncate mr-2">{player.name}</span>
                  <span className="font-semibold">{(gameState.gameTheoryWheelForces?.[player.id] ?? 0).toFixed(1)}</span>
                </li>
              ))}
            </ul>
          </div>
          {isHost ? (
            <button
              type="button"
              onClick={handleNextRound}
              className="w-full px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              {gtw.nextRound}
            </button>
          ) : (
            <p className="text-sm text-gray-600 text-center">{gtw.waitingForHostNextRound}</p>
          )}
        </div>
      )}
    </div>
  );
};
